/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { compose } from 'recompose';

import type { Theme } from '@coursera/cds-core';
import { breakpoints, useTheme } from '@coursera/cds-core';

import { ClipsVideoRootContainer } from 'bundles/clips/components/ClipsVideoRootContainer';
import type { ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import withSFCVLPData from 'bundles/clips/components/short-form-content/withSFCVLPData';
import type { Course, OverridePartnerLogos, VLPItem, VLPLesson, XDPData } from 'bundles/clips/types/vlpSharedTypes';
import { NotFound } from 'bundles/common/components/withNotFound';

import { ClipsErrorMessage } from './ClipsErrorMessage';

type PropsFromWithSFCVLPData = {
  courseSlug: string;
  programSlug?: string;
  courseId: string;
  itemId: string;
  course: Course;
  summary: string | null;
  onDemandCourse: {
    overridePartnerLogos?: OverridePartnerLogos;
  };
  currentLesson?: VLPLesson;
  lessons: Array<VLPLesson>;
  currentVideoItem: VLPItem;
  lectureItems: Array<VLPItem>;
  xdpApiData: XDPData;
  gqlLoading?: boolean;
  csrError?: boolean;
  views?: number;
  preferredAvailableForEnrollmentProgram?: ProgramSuggestion;
  userEnrolledIntoCourse?: boolean;
  likes?: number;
  accessProvidedViaProgram?: ProgramSuggestion;
};

export type Props = PropsFromWithSFCVLPData;

const clipsRootContainer = (theme: Theme) =>
  css({
    paddingTop: 'var(--cds-spacing-300)',
    margin: '0 auto',
    display: 'flex',
    gap: '36px',
    [breakpoints.down('sm')]: {
      padding: '16px 16px 40px 16px',
    },
    [breakpoints.down('xs')]: {
      padding: '16px 0 40px 0',
    },
  });

export function ClipsRootContainer({
  gqlLoading,
  csrError,
  course,
  courseSlug,
  programSlug,
  courseId,
  itemId,
  onDemandCourse,
  lectureItems,
  lessons,
  currentLesson,
  currentVideoItem,
  views,
  likes,
  xdpApiData,
  userEnrolledIntoCourse,
  preferredAvailableForEnrollmentProgram,
  accessProvidedViaProgram,
  summary,
}: Props) {
  const theme = useTheme();

  if (!gqlLoading && csrError) {
    return <NotFound description={<ClipsErrorMessage />} />;
  }

  const skills = xdpApiData?.xdpMetadata?.cdpMetadata?.skills ?? [];
  const overridePartnerLogos = onDemandCourse?.overridePartnerLogos ?? {};

  return (
    <div css={clipsRootContainer(theme)} data-testid="clips-root-container">
      <ClipsVideoRootContainer
        course={course}
        courseId={courseId}
        itemId={itemId}
        courseSlug={courseSlug}
        programSlug={programSlug}
        currentVideoItem={currentVideoItem}
        lectureItems={lectureItems}
        currentLesson={currentLesson}
        preferredAvailableForEnrollmentProgram={preferredAvailableForEnrollmentProgram}
        skills={skills as Array<string>}
        views={views}
        likes={likes}
        accessProvidedViaProgram={accessProvidedViaProgram}
        overridePartnerLogos={overridePartnerLogos}
        xdpApiData={xdpApiData}
        userEnrolledIntoCourse={userEnrolledIntoCourse}
        lessons={lessons}
        gqlLoading={Boolean(gqlLoading)}
        summary={summary}
      />
    </div>
  );
}

export default compose<Props, {}>(withSFCVLPData)(ClipsRootContainer);
